/**
 * Reads what the SDMS Template Builder needs from a template .xlsx straight from the
 * package XML — its sheets, its mail-merge bands (sheet-local defined names, which
 * ExcelJS's model drops) and the cells holding a formula. Read-only: the workbook is
 * only ever changed server-side by the surgical patcher.
 */
import type { BandName, TemplateParsing } from './sdmsTemplateApi';

export const BAND_NAMES: readonly BandName[] = ['HEADERRANGE', 'DETAILRANGE', 'CHEADER', 'CDETAIL'];
export const BAND_LABEL: Record<BandName, string> = {
  HEADERRANGE: 'Header', DETAILRANGE: 'Detail', CHEADER: 'Calibration Header', CDETAIL: 'Calibration Detail',
};

export interface WorkbookBand { sheetIndex: number; name: BandName; range: string }
export interface WorkbookFormulaCell { sheetIndex: number; ref: string; formula: string }
/** One conditional-formatting rule as the file holds it. */
export interface WorkbookCfRule {
  sheetIndex: number;
  range: string;
  type: string;
  operator: string | null;
  formulas: string[];
  text: string | null;
  priority: number;
  fontColor: string | null;
  fillColor: string | null;
  bold: boolean;
  stopIfTrue: boolean;
}
/** One data-validation rule as the file holds it (Excel <dataValidation>). */
export interface WorkbookDvRule {
  sheetIndex: number;
  /** 0-based position in the sheet's <dataValidations> list — how the server finds it again */
  index: number;
  range: string;
  /** list · whole · decimal · date · time · textLength · custom · any (no type attribute) */
  type: string;
  /** Excel's default when absent: between */
  operator: string;
  formula1: string | null;
  formula2: string | null;
  allowBlank: boolean;
  /** Excel stores the INVERSE (showDropDown="1" = arrow hidden) */
  inCellDropdown: boolean;
  showInputMessage: boolean;
  inputTitle: string | null;
  inputMessage: string | null;
  showErrorMessage: boolean;
  errorStyle: string;
  errorTitle: string | null;
  errorMessage: string | null;
}
export interface WorkbookInfo {
  sheets: string[];
  bands: WorkbookBand[];
  /** every formula cell, `=`-prefixed */
  formulas: WorkbookFormulaCell[];
  conditionalFormats: WorkbookCfRule[];
  /** the `<dataValidations>` rules, each sheet in file order (absent when none) */
  dataValidations?: WorkbookDvRule[];
}

const EMPTY: WorkbookInfo = { sheets: [], bands: [], formulas: [], conditionalFormats: [] };

/** ARGB / RGB attribute → #RRGGBB. */
function hexOf(attr: string | undefined): string | null {
  if (!attr) return null;
  const h = attr.length === 8 ? attr.slice(2) : attr;
  return /^[0-9a-f]{6}$/i.test(h) ? `#${h.toUpperCase()}` : null;
}

/** The differential formats (dxfs) conditional rules point at: colours + bold only. */
function readDxfs(stylesXml: string | undefined): { fontColor: string | null; fillColor: string | null; bold: boolean }[] {
  const block = /<dxfs\b[^>]*>([\s\S]*?)<\/dxfs>/.exec(stylesXml ?? '')?.[1] ?? '';
  return [...block.matchAll(/<dxf>([\s\S]*?)<\/dxf>|<dxf\/>/g)].map(m => {
    const body = m[1] ?? '';
    const font = /<font>([\s\S]*?)<\/font>/.exec(body)?.[1] ?? '';
    const fill = /<fill>([\s\S]*?)<\/fill>/.exec(body)?.[1] ?? '';
    return {
      fontColor: hexOf(/<color\b[^>]*\brgb="([0-9A-Fa-f]+)"/.exec(font)?.[1]),
      fillColor: hexOf(/<bgColor\b[^>]*\brgb="([0-9A-Fa-f]+)"/.exec(fill)?.[1] ?? /<fgColor\b[^>]*\brgb="([0-9A-Fa-f]+)"/.exec(fill)?.[1]),
      bold: /<b\s*\/>|<b\s+val="(1|true)"/.test(font),
    };
  });
}

function decode(s: string): string {
  return s.replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

/**
 * A worksheet's `<dataValidations>` rules, in file order (the order the server addresses
 * them by). Only the main block — Excel 2010 keeps cross-sheet list rules in an x14
 * `extLst`, which the builder neither shows nor touches. Exported for tests.
 */
export function readDataValidations(sheetXml: string, sheetIndex: number): WorkbookDvRule[] {
  const block = /<dataValidations\b[^>]*>([\s\S]*?)<\/dataValidations>/.exec(sheetXml)?.[1];
  if (!block) return [];
  const out: WorkbookDvRule[] = [];
  let index = 0;
  for (const m of block.matchAll(/<dataValidation\b([^>]*?)(?:\/>|>([\s\S]*?)<\/dataValidation>)/g)) {
    const attrs = m[1]!;
    const attr = (n: string) => { const v = new RegExp(`\\b${n}="([^"]*)"`).exec(attrs)?.[1]; return v == null ? null : decode(v); };
    const on = (n: string) => attr(n) === '1' || attr(n) === 'true';
    const formula = (n: string) => { const v = new RegExp(`<${n}>([\\s\\S]*?)</${n}>`).exec(m[2] ?? '')?.[1]; return v == null ? null : decode(v); };
    out.push({
      sheetIndex, index: index++,
      range: (attr('sqref') ?? '').replace(/\$/g, '').toUpperCase().trim(),
      type: attr('type') ?? 'any',
      operator: attr('operator') ?? 'between',
      formula1: formula('formula1'), formula2: formula('formula2'),
      allowBlank: on('allowBlank'),
      inCellDropdown: !on('showDropDown'),
      showInputMessage: on('showInputMessage'), inputTitle: attr('promptTitle'), inputMessage: attr('prompt'),
      showErrorMessage: on('showErrorMessage'), errorStyle: attr('errorStyle') ?? 'stop',
      errorTitle: attr('errorTitle'), errorMessage: attr('error'),
    });
  }
  return out;
}

/** `'Raw Data'!$A$7:$F$7` → `A7:F7` (the sheet comes from localSheetId). */
export function stripRange(ref: string): string {
  const bang = ref.lastIndexOf('!');
  return (bang >= 0 ? ref.slice(bang + 1) : ref).replace(/\$/g, '').toUpperCase().trim();
}

/** Parses the workbook's sheets, bands and formula cells; empty info for an unreadable file. */
export async function readTemplateWorkbook(buf: ArrayBuffer | null): Promise<WorkbookInfo> {
  if (!buf || buf.byteLength === 0) return EMPTY;
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(buf);
    const wbXml = await zip.file('xl/workbook.xml')?.async('string');
    const relsXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
    if (!wbXml) return EMPTY;

    const targets = new Map<string, string>();
    for (const m of (relsXml ?? '').matchAll(/<Relationship\b([^>]*)\/?>/g)) {
      const id = /\bId="([^"]+)"/.exec(m[1]!)?.[1];
      const target = /\bTarget="([^"]+)"/.exec(m[1]!)?.[1];
      if (id && target) targets.set(id, target.startsWith('/') ? target.slice(1) : `xl/${target}`);
    }

    const sheets: { name: string; part: string | undefined }[] = [];
    for (const m of wbXml.matchAll(/<sheet\b([^>]*)\/?>/g)) {
      const name = decode(/\bname="([^"]*)"/.exec(m[1]!)?.[1] ?? '');
      const rid = /\br:id="([^"]+)"/.exec(m[1]!)?.[1] ?? '';
      sheets.push({ name, part: targets.get(rid) });
    }

    const bands: WorkbookBand[] = [];
    for (const m of wbXml.matchAll(/<definedName\b([^>]*)>([^<]*)<\/definedName>/g)) {
      const name = (/\bname="([^"]+)"/.exec(m[1]!)?.[1] ?? '').toUpperCase() as BandName;
      if (!BAND_NAMES.includes(name)) continue;
      const local = /\blocalSheetId="(\d+)"/.exec(m[1]!)?.[1];
      const value = decode(m[2]!);
      // A global name is tied to the sheet its reference names.
      let sheetIndex = local != null ? Number(local) + 1 : 0;
      if (!sheetIndex) {
        const sheetName = /^'?(.+?)'?!/.exec(value)?.[1]?.replace(/''/g, "'");
        sheetIndex = Math.max(1, sheets.findIndex(s => s.name === sheetName) + 1);
      }
      bands.push({ sheetIndex, name, range: stripRange(value) });
    }

    const dxfs = readDxfs(await zip.file('xl/styles.xml')?.async('string'));
    const formulas: WorkbookFormulaCell[] = [];
    const conditionalFormats: WorkbookCfRule[] = [];
    const dataValidations: WorkbookDvRule[] = [];
    for (let i = 0; i < sheets.length; i++) {
      const part = sheets[i]!.part;
      const xml = part ? await zip.file(part)?.async('string') : undefined;
      if (!xml) continue;
      for (const c of xml.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const f = /<f\b[^>]*>([\s\S]*?)<\/f>/.exec(c[2] ?? '')?.[1];
        const ref = /\br="([A-Z]+\d+)"/i.exec(c[1]!)?.[1];
        if (f && ref) formulas.push({ sheetIndex: i + 1, ref: ref.toUpperCase(), formula: `=${decode(f)}` });
      }
      for (const block of xml.matchAll(/<conditionalFormatting\b([^>]*)>([\s\S]*?)<\/conditionalFormatting>/g)) {
        const range = (/\bsqref="([^"]+)"/.exec(block[1]!)?.[1] ?? '').replace(/\$/g, '').toUpperCase();
        for (const rule of block[2]!.matchAll(/<cfRule\b([^>]*?)(?:\/>|>([\s\S]*?)<\/cfRule>)/g)) {
          const attrs = rule[1]!;
          const attr = (n: string) => new RegExp(`\\b${n}="([^"]*)"`).exec(attrs)?.[1];
          const dxf = dxfs[Number(attr('dxfId') ?? -1)];
          conditionalFormats.push({
            sheetIndex: i + 1,
            range,
            type: attr('type') ?? '',
            operator: attr('operator') ?? null,
            formulas: [...(rule[2] ?? '').matchAll(/<formula>([\s\S]*?)<\/formula>/g)].map(m => decode(m[1]!)),
            text: attr('text') != null ? decode(attr('text')!) : null,
            priority: Number(attr('priority') ?? 0),
            fontColor: dxf?.fontColor ?? null,
            fillColor: dxf?.fillColor ?? null,
            bold: dxf?.bold ?? false,
            stopIfTrue: attr('stopIfTrue') === '1' || attr('stopIfTrue') === 'true',
          });
        }
      }
      dataValidations.push(...readDataValidations(xml, i + 1));
    }
    return { sheets: sheets.map(s => s.name), bands, formulas, conditionalFormats, dataValidations };
  } catch {
    return EMPTY;       // not an xlsx the reader understands — the builder shows it as "no workbook"
  }
}

// ── A1 helpers ──────────────────────────────────────────────────────────────

export function colName(col0: number): string {
  let n = col0 + 1;
  let s = '';
  for (; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

export function colIndex(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** 0-based row/col → A1. */
export function a1(row0: number, col0: number): string { return `${colName(col0)}${row0 + 1}`; }

/** A1 → 0-based {row, col}; null when invalid. */
export function parseA1(ref: string | null | undefined): { row: number; col: number } | null {
  const m = /^\$?([A-Z]{1,3})\$?(\d{1,7})$/i.exec((ref ?? '').trim());
  if (!m) return null;
  const row = Number(m[2]) - 1;
  return row < 0 ? null : { row, col: colIndex(m[1]!) };
}

/** Is `ref` inside the A1 `range` (e.g. `A7:AF7`)? */
export function inRange(ref: string | null | undefined, range: string | null | undefined): boolean {
  const cell = parseA1(ref);
  const [a, b] = (range ?? '').split(':');
  const from = parseA1(a);
  const to = parseA1(b ?? a);
  if (!cell || !from || !to) return false;
  return cell.row >= Math.min(from.row, to.row) && cell.row <= Math.max(from.row, to.row)
      && cell.col >= Math.min(from.col, to.col) && cell.col <= Math.max(from.col, to.col);
}

/** The merge-field formula legacy templates carry. */
export function fieldFormula(fieldName: string): string {
  return `=FIELD("${fieldName.trim().toUpperCase()}")`;
}

/** Same comparison the server uses: no leading '=', no surrounding blanks. */
export function sameFormula(a: string | null | undefined, b: string | null | undefined): boolean {
  const strip = (f: string | null | undefined) => (f ?? '').trim().replace(/^=/, '').trim();
  return strip(a) === strip(b);
}

/** The band ranges in force for a sheet: the workbook's, overridden by pending edits. */
export function effectiveBands(info: WorkbookInfo, pending: { sheetIndex: number; name: BandName; range: string | null }[]): WorkbookBand[] {
  const map = new Map<string, WorkbookBand | null>();
  for (const b of info.bands) map.set(`${b.sheetIndex}|${b.name}`, b);
  for (const p of pending) map.set(`${p.sheetIndex}|${p.name}`, p.range ? { sheetIndex: p.sheetIndex, name: p.name, range: p.range } : null);
  return [...map.values()].filter((b): b is WorkbookBand => b != null);
}

/** Legacy SheetID: 1-based, 0 means the first sheet. */
export function sheetOf(p: Pick<TemplateParsing, 'sheetID'>): number { return p.sheetID < 1 ? 1 : p.sheetID; }
